/**
 * A Reading — the one component this dashboard is built around.
 *
 * §2 requires every metric to carry units, observation time, environment, provenance and
 * freshness, and requires a missing metric to read Unknown rather than zero or Healthy.
 *
 * The usual way to honour that is a tooltip, which makes provenance something you have to go
 * looking for. Here it is part of the component: you cannot render a number on this surface
 * without saying where it came from and when it was seen.
 *
 * The deliberate choice: an `unknown` Reading occupies the same space and the same slot as a
 * live one. Greying absence out, or omitting the card entirely, is how a dashboard ends up
 * quietly reporting "fine" when it means "I cannot see". Dimmed, yes — hidden, never.
 */

export type ReadingState =
  /** Scraped recently; the number is current. */
  | 'live'
  /** Was collected, but the last observation is older than the view's freshness budget. */
  | 'stale'
  /** No source exists. Not zero, not healthy — not measured. */
  | 'unknown'
  /** A value exists but has not been established by evidence (capacity, §9). */
  | 'unverified'
  /** Measured once by an approved test on the real system, with evidence (a floor on a date, D3). */
  | 'verified'
  /** The source exists and failed. Different from unknown, and worth saying so. */
  | 'error';

export interface ReadingProps {
  label: string;
  /** Formatted value. Pass undefined for an unknown reading — never pass 0 to mean "no data". */
  value?: string | undefined;
  unit?: string | undefined;
  state: ReadingState;
  /** Where the number came from: an exporter, a job, a calculation. */
  source: string;
  /** When it was observed. Omit only when state is `unknown`. */
  observedAt?: Date | undefined;
  environment?: string | undefined;
  /** Why it is unknown, unverified or errored. Required for those states. */
  reason?: string | undefined;
}

const STATE_WORD: Record<ReadingState, string> = {
  live: 'live',
  stale: 'stale',
  unknown: 'no signal',
  unverified: 'unverified',
  verified: 'verified',
  error: 'error',
};

export function relativeTime(then: Date, now: Date = new Date()): string {
  const s = Math.round((now.getTime() - then.getTime()) / 1000);
  // "observed 0s ago" reads like a glitch. Under five seconds is just now.
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

export function Reading(props: ReadingProps) {
  const { label, value, unit, state, source, observedAt, environment, reason } = props;

  // A reading with no value is Unknown regardless of what the caller claimed, so a bug
  // upstream cannot render an empty card as if it were live.
  const effective: ReadingState = value === undefined && state !== 'unverified' ? 'unknown' : state;
  const shown = value ?? 'Unknown';

  return (
    <article className="reading" data-state={effective}>
      <div className="top">
        <span className="label">{label}</span>
        {/* Decorative: the state word appears in .prov below, so colour is never the only cue. */}
        <span className="pip" aria-hidden="true" />
      </div>

      <div className="value">
        {shown}
        {value !== undefined && unit ? <span className="unit">{unit}</span> : null}
      </div>

      <div className="prov">
        <b>{STATE_WORD[effective]}</b>
        {' · '}
        {source}
        {observedAt ? <> {' · '} observed {relativeTime(observedAt)}</> : null}
        {environment ? <> {' · '} {environment}</> : null}
        {reason ? <><br />{reason}</> : null}
      </div>
    </article>
  );
}

/**
 * A view with no data source at all.
 *
 * Deliberately not a spinner and not an empty state: those both imply the data is coming.
 * This says what is missing, why, and what would fix it.
 */
export function NoSource({ what, why, unblock }: { what: string; why: string; unblock: string }) {
  return (
    <div className="notice">
      <h3>{what} is not being measured</h3>
      <p>
        {why} This view shows nothing rather than an estimate, because an estimate here would be
        indistinguishable from a measurement. {unblock}
      </p>
    </div>
  );
}
